import { fetchWorktreeDetail } from "@/lib/worktree-query";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Chat, Repo, Worktree, WorktreeDetailResponse } from "@rivets/core";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { useOrgSocket } from "@/lib/socket";
import { StatusDot } from "@/components/StatusDot";
import { compactText, useConnectionStatus, type AppConnectionState } from "@/lib/connectionState";
import { useOrgContext } from "@/lib/OrgContext";
import { useCommandPalette } from "@/lib/paletteStore";
import { OrgIdentity } from "@/components/OrgIdentity";
import { AccountMenu } from "@/components/AccountMenu";
import { RepoIdentity } from "@/components/RepoIdentity";
import { NewTaskModal } from "@/features/worktrees/NewTaskModal";
import { NavigationIcon as Icon, type NavigationIconName } from "@/components/NavigationIcon";
import { shortcutChordLabel, useKeyboardShortcutSettings, useShortcutCommand, useTaskShortcutCommands } from "@/lib/keyboardShortcuts";
import { useNavigationHistory } from "@/lib/navigationHistory";
import { useScrollEdges } from "@/lib/scrollEdges";
import { ContextMenu, useContextMenu } from "@/components/ContextMenu";
import { InlineRenameField } from "@/components/InlineRenameField";
import { renameWorktree } from "@/lib/worktreeActions";
import { useAssistantDrawer } from "@/features/assistant/assistantStore";
import { setRepoCollapsed, useCollapsedRepos, useSidebarLayoutActions } from "@/lib/sidebarLayout";
import { useDragReorder } from "@/lib/useDragReorder";
import { useRowTransitions } from "@/lib/rowTransitions";
import { applyOrder, moveItem } from "@/lib/reorder";
import { signOutAndRedirect } from "@/lib/auth-client";
import { intentPrefetchHandlers } from "@/lib/prefetch";
import { useCacheState } from "@/lib/persistence";
import { worktreeActivity } from "@/lib/taskStatus";

/** Module-level so `useClosingRows` is handed the same function every render. */
const worktreeId = (worktree: Worktree) => worktree.id;

export function Sidebar({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const { orgId, org, role } = useOrgContext();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const status = useOrgSocket((state) => state.status);
  const protocolError = useOrgSocket((state) => state.protocolError);
  const setPaletteOpen = useCommandPalette((state) => state.setOpen);
  const assistantOpen = useAssistantDrawer((state) => state.open);
  const assistantUnread = useAssistantDrawer((state) => state.unread);
  const toggleAssistant = useAssistantDrawer((state) => state.toggle);
  // Actions only: the sidebar never reads `collapsed` (the stylesheet does),
  // and subscribing to it would re-render the whole tree on its way out of view.
  const { collapse } = useSidebarLayoutActions();
  // Every chord this header prints comes from the live bindings, so a rebind in
  // Settings reaches the tooltips too.
  const bindings = useKeyboardShortcutSettings((state) => state.bindings);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  // Stable, so the account menu's document-level dismiss listeners are
  // registered once rather than on every render of this tree.
  const closeAccountMenu = useCallback(() => setAccountMenuOpen(false), []);
  // Right-click on a task row: the same items the home list offers, minus the
  // ones that need the row's own hover controls to make sense here.
  const rowMenu = useContextMenu();
  const [renamingWorktreeId, setRenamingWorktreeId] = useState<string | null>(null);
  const orgTriggerRef = useRef<HTMLButtonElement>(null);
  // The project tree's top and bottom fades follow the scroll position: a
  // three-repo list that fits must not have its first row dimmed.
  const projectsRef = useScrollEdges("y");
  const collapsedRepos = useCollapsedRepos(orgId);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newTaskRepoId, setNewTaskRepoId] = useState<string>();
  const [newTaskKind, setNewTaskKind] = useState<"task" | "repo">("task");

  const { data: me } = useQuery({
    queryKey: qk.me(),
    queryFn: ({ signal }: { signal: AbortSignal }) => client.withSignal(signal).me(),
    staleTime: 30_000,
  });
  const reposQuery = useQuery({
    queryKey: qk.repos(orgId),
    queryFn: ({ signal }: { signal: AbortSignal }) => client.withSignal(signal).listRepos(orgId),
    enabled: Boolean(orgId),
  });
  const repos = reposQuery.data?.repos ?? [];
  const repoIds = useMemo(() => repos.map((repo) => repo.id), [repos]);

  /* Project order is a personal preference the API keeps per member, so the
     drag writes it there and every client the account signs into opens in the
     same order. The cache is moved first: the list is already sitting in the
     new order when the pointer comes up, and the request only confirms it. */
  const commitRepoOrder = useCallback((order: string[]) => {
    queryClient.setQueryData<{ repos: Repo[] }>(qk.repos(orgId), (old) => old ? { repos: applyOrder(old.repos, order) } : old);
    client.reorderRepos(orgId, order)
      .then((result) => queryClient.setQueryData(qk.repos(orgId), result))
      .catch(() => queryClient.invalidateQueries({ queryKey: qk.repos(orgId) }));
  }, [orgId, queryClient]);
  const repoDrag = useDragReorder({ ids: repoIds, onCommit: commitRepoOrder });
  // The same move without a pointer: ⌥↑ / ⌥↓ on a focused project row.
  const nudgeRepo = useCallback((repoId: string, offset: number) => {
    const from = repoIds.indexOf(repoId);
    const to = from + offset;
    if (from < 0 || to < 0 || to >= repoIds.length) return;
    commitRepoOrder(moveItem(repoIds, from, to));
  }, [commitRepoOrder, repoIds]);
  const onTreeKeys = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.altKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
      const repoId = (event.target as HTMLElement).closest<HTMLElement>("[data-reorder-item]")?.dataset.reorderId;
      if (!repoId) return;
      event.preventDefault();
      nudgeRepo(repoId, event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    handleTreeKeys(event);
  }, [nudgeRepo]);

  const worktreeQueries = useQueries({
    queries: repos.map((repo) => ({
      queryKey: qk.worktrees(repo.id),
      queryFn: ({ signal }: { signal: AbortSignal }) => client.withSignal(signal).listWorktrees(repo.id),
      enabled: !collapsedRepos.has(repo.id),
      staleTime: 15_000,
    })),
  });
  const liveWorktrees = useMemo(
    () => repos.flatMap((repo, index) => (worktreeQueries[index]?.data?.worktrees ?? []).filter((worktree) => worktree.status !== "archived")),
    [repos, worktreeQueries],
  );
  // A task archived anywhere — this tree, its own page, or a pull request
  // closing under it — leaves this list. Its row is held for the length of the
  // closing animation so the tree shuts the gap rather than the row blinking
  // out (FRNT-753). A task created anywhere opens into it the same way.
  const { rows: treeWorktrees, closing: closingWorktrees, opening: openingWorktrees } = useRowTransitions(liveWorktrees, worktreeId);
  const worktreesByRepo = useMemo(() => {
    const map = new Map<string, Worktree[]>();
    repos.forEach((repo) => map.set(repo.id, []));
    treeWorktrees.forEach((worktree) => map.get(worktree.repoId)?.push(worktree));
    return map;
  }, [repos, treeWorktrees]);
  const visibleWorktrees = useMemo(
    () => repos.flatMap((repo) => (collapsedRepos.has(repo.id) ? [] : worktreesByRepo.get(repo.id) ?? [])),
    [collapsedRepos, repos, worktreesByRepo],
  );
  const detailQueries = useQueries({
    queries: visibleWorktrees.map((worktree) => ({
      queryKey: qk.worktree(worktree.id),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchWorktreeDetail(queryClient, worktree.id, signal),
      staleTime: 15_000,
    })),
  });
  const detailsByWorktree = useMemo(() => {
    const map = new Map<string, WorktreeDetailResponse>();
    visibleWorktrees.forEach((worktree, index) => {
      const detail = detailQueries[index]?.data;
      if (detail) map.set(worktree.id, detail);
    });
    return map;
  }, [detailQueries, visibleWorktrees]);

  // The app's connection, debounced (CONTRACT C1): nothing while connected,
  // and a blip shorter than 2 s never shows. A shell restored from this
  // browser says so until the stream is up.
  const presented = useConnectionStatus((state) => state.presented);
  const restoredFromCache = useCacheState((state) => state.restored);
  const connectionSettled = presented === "connected" && (status === "open" || !restoredFromCache || status === "idle");
  const connectionText = presented === "updateRequired" && protocolError
    ? protocolError
    : compactText(presented) ?? (restoredFromCache && status !== "open" ? "Showing saved data" : "Live");

  const basePath = `/o/${orgId}`;
  const activeWorktreeId = location.pathname.match(/\/wt\/([^/]+)/)?.[1];
  const openNewTask = (repoId?: string, kind: "task" | "repo" = "task") => {
    setNewTaskRepoId(repoId);
    setNewTaskKind(kind);
    setNewTaskOpen(true);
  };
  useShortcutCommand("newTask", () => {
    openNewTask();
    onClose?.();
  });
  useShortcutCommand("openSettings", () => {
    onClose?.();
    navigate({ to: `${basePath}/settings` });
  });
  useTaskShortcutCommands((index) => {
    const worktree = visibleWorktrees[index];
    if (!worktree) return;
    onClose?.();
    if (activeWorktreeId === worktree.id) return;
    navigate({ to: `/o/${orgId}/wt/${worktree.id}` });
  });

  return (
    <aside className={`sidebar${open ? " open" : ""}`} aria-label="Primary navigation">
      {/* Org selector, then the things you do from anywhere: retrace your steps,
          find, write, get the navigation out of the way. The brand logo is gone
          — you know which app you are in; what you need told is which
          workspace. */}
      <header className="sidebar-header">
        <div className="sidebar-org-selector">
          <button
            ref={orgTriggerRef}
            type="button"
            className="sidebar-org-trigger"
            /* The badge is decoration; a screen reader hears the trouble here,
               in the one accessible name this control has. */
            aria-label={connectionSettled ? "Switch organization" : `Switch organization — ${connectionText}`}
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
            title={org?.name ?? "No organization"}
            onClick={() => setAccountMenuOpen((value) => !value)}
          >
            <OrgIdentity
              image={org?.image}
              name={org?.name}
              size={18}
              /* A permanently green dot on the control you look at most is
                 noise. The badge appears only while the stream is in trouble. */
              status={connectionSettled ? undefined : <StatusDot tone={presented === "connected" ? "amber" : connectionTone(presented)} pulse={presented !== "offline" && presented !== "updateRequired"} />}
              statusLabel={connectionText}
            />
            <span className="sidebar-org-name truncate">{org?.name ?? "No organization"}</span>
            {/* The compact label beside the organization, as on the Mac. */}
            {compactText(presented) ? <span className="sidebar-connection-label" data-state={presented}>{compactText(presented)}</span> : null}
            <Icon name="chevronDown" className="sidebar-org-chevron" />
          </button>
          <AccountMenu
            open={accountMenuOpen}
            onClose={closeAccountMenu}
            triggerRef={orgTriggerRef}
            orgId={orgId}
            orgs={me?.orgs ?? []}
            userEmail={me?.user.email}
            isSuperAdmin={me?.user.isSuperAdmin}
            onSettings={() => { onClose?.(); navigate({ to: `${basePath}/settings` }); }}
            onMembers={() => { onClose?.(); navigate({ to: `${basePath}/settings`, hash: "members" }); }}
            onPlatformAdmin={() => { onClose?.(); navigate({ to: "/admin" }); }}
            onSelectOrg={(candidateId) => { onClose?.(); navigate({ to: `/o/${candidateId}` }); }}
            onSignOut={() => { onClose?.(); signOutAndRedirect(); }}
          />
        </div>
        <div className="sidebar-header-actions">
          {/* Linear's ‹ ›: the pair that makes a deep task page a place you can
              back out of, not a dead end. They stay in the mobile drawer — a
              phone browser hides its own chrome as you scroll. */}
          <HistoryArrows onClose={onClose} />
          <button
            type="button"
            className="sidebar-header-button"
            aria-label="Search and commands"
            title={`Search (${shortcutChordLabel(bindings.commandPalette)})`}
            onClick={() => { setPaletteOpen(true); onClose?.(); }}
          >
            <Icon name="search" />
          </button>
          <button
            type="button"
            className="sidebar-header-button"
            aria-label="New task"
            title={`New task (${shortcutChordLabel(bindings.newTask)})`}
            onClick={() => { openNewTask(); onClose?.(); }}
          >
            <Icon name="compose" />
          </button>
          {/* Desktop only: the mobile drawer already closes on its own scrim. */}
          <button
            type="button"
            className="sidebar-header-button sidebar-collapse-button"
            aria-label="Collapse navigation"
            title={`Collapse navigation (${shortcutChordLabel(bindings.toggleSidebar)})`}
            onClick={() => { collapse(); onClose?.(); }}
          >
            <Icon name="panelLeft" />
          </button>
        </div>
      </header>

      <nav className="sidebar-primary" aria-label="Primary navigation">
        <TreeLink to={basePath} active={location.pathname === basePath || location.pathname === `${basePath}/`} icon="home" onClick={onClose}>
          Home
        </TreeLink>
        <TreeLink to={`${basePath}/automations`} active={location.pathname.startsWith(`${basePath}/automations`)} icon="automation" onClick={onClose}>
          Automations
        </TreeLink>
        {/* The assistant is a destination, not a footer switch: one row under
            Home, active while the drawer is open, with a dot when it replied
            while the drawer was shut. */}
        <button
          type="button"
          className={`sidebar-tree-row sidebar-action-row sidebar-assistant-row${assistantOpen ? " active" : ""}`}
          data-tree-row
          data-depth="0"
          aria-pressed={assistantOpen}
          onClick={() => { toggleAssistant(); onClose?.(); }}
        >
          <span className="sidebar-assistant-glyph">
            <AssistantGlyph />
            {assistantUnread && !assistantOpen ? <span className="sidebar-assistant-unread" aria-label="Unread reply" /> : null}
          </span>
          <span className="sidebar-row-label">Assistant</span>
          <span className="sidebar-shortcut">{shortcutChordLabel(bindings.toggleAssistant)}</span>
        </button>
      </nav>

      <div className="sidebar-projects-wrap">
        <nav ref={projectsRef} className="sidebar-tree sidebar-projects" aria-label="Projects" onKeyDown={onTreeKeys}>
          <div className="sidebar-section-heading">
            <span>Projects</span>
            <span className="sidebar-section-count">{repos.length || ""}</span>
          </div>

          {!reposQuery.isPending && repos.length === 0 && (
            <div className="sidebar-empty-tree">
              <strong>{role === "owner" ? "Bring in a repository" : "No repository access"}</strong>
              <span>{role === "owner" ? "Your tasks will live here." : "Ask an organization owner to add you to a team."}</span>
              {role === "owner" && <button type="button" className="sidebar-empty-cta" onClick={() => openNewTask(undefined, "repo")}>Connect repository</button>}
            </div>
          )}

          {repos.map((repo, repoIndex) => {
            const expanded = !collapsedRepos.has(repo.id);
            const worktrees = worktreesByRepo.get(repo.id) ?? [];
            const loading = worktreeQueries[repos.indexOf(repo)]?.isPending;
            return (
              /* The group is the drag unit: a project and every task under it
                 move together, and the row itself is the grip — no handle to
                 find, and ⌥↑ / ⌥↓ for anyone not using a pointer. */
              <div className="sidebar-repo-group" key={repo.id} data-reorder-item data-reorder-id={repo.id}>
                <div
                  className="sidebar-repo-row-wrap"
                  onPointerDown={(event) => repoDrag.onPointerDown(event, repo.id)}
                  /* The same move as the drag, for a right-click and for
                     anyone who never sees the pointer affordance. */
                  onContextMenu={(event) => rowMenu.open(event, [
                    { id: "move-up", label: "Move up", disabled: repoIndex === 0, onSelect: () => nudgeRepo(repo.id, -1) },
                    { id: "move-down", label: "Move down", disabled: repoIndex === repos.length - 1, onSelect: () => nudgeRepo(repo.id, 1) },
                    { separator: true },
                    { id: "new-task", label: `New task in ${repo.name}`, onSelect: () => openNewTask(repo.id) },
                  ])}
                >
                  <button
                    className="sidebar-tree-row sidebar-repo-row"
                    type="button"
                    data-tree-row
                    data-depth="0"
                    data-expandable="true"
                    data-expanded={expanded}
                    aria-expanded={expanded}
                    aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
                    onClick={() => setRepoCollapsed(orgId, repo.id, expanded)}
                  >
                    <Icon name="chevron" className={`sidebar-disclosure${expanded ? " expanded" : ""}`} />
                    <RepoIdentity repo={repo} />
                    <span className="sidebar-row-label truncate">{repo.name}</span>
                  </button>
                  <div className="sidebar-repo-controls" data-no-drag>
                    <button className="sidebar-repo-add" type="button" aria-label={`New task in ${repo.name}`} onClick={() => openNewTask(repo.id)}><Icon name="plus" /></button>
                  </div>
                </div>

                {expanded && worktrees.map((worktree) => {
                  const detail = detailsByWorktree.get(worktree.id);
                  const chats = detail?.chats ?? [];
                  const active = activeWorktreeId === worktree.id;
                  return (
                    <div
                      key={worktree.id}
                      className={`sidebar-worktree-group${closingWorktrees.has(worktree.id) ? " closing" : ""}${openingWorktrees.has(worktree.id) ? " opening" : ""}`}
                      /* Already gone as far as anything that reads the tree is
                         concerned; what is left is the row shutting. `inert`
                         rather than `aria-hidden`, because the row still holds
                         a link — hiding a focusable node from the a11y tree
                         while it can still be tabbed to is the worse fault. */
                      inert={closingWorktrees.has(worktree.id) || undefined}
                    >
                      <Link
                        to={`/o/${orgId}/wt/${worktree.id}`}
                        className={`sidebar-tree-row sidebar-worktree-row${active ? " active" : ""}`}
                        data-tree-row
                        data-depth="1"
                        {...(active ? {} : intentPrefetchHandlers(queryClient, worktree.id))}
                        onContextMenu={(event) => rowMenu.open(event, [
                          { id: "open", label: "Open task", disabled: active, onSelect: () => { onClose?.(); navigate({ to: `/o/${orgId}/wt/${worktree.id}` }); } },
                          { id: "rename", label: "Rename task", onSelect: () => setRenamingWorktreeId(worktree.id) },
                          { id: "copy-branch", label: "Copy branch name", onSelect: () => { void navigator.clipboard?.writeText(worktree.branch); } },
                          ...(worktree.source?.url
                            ? [{ id: "source", label: `Open ${worktree.source.label}`, onSelect: () => window.open(worktree.source!.url!, "_blank", "noreferrer") }]
                            : []),
                          { separator: true },
                          { id: "new-task", label: `New task in ${repo.name}`, onSelect: () => openNewTask(repo.id) },
                        ])}
                        onClick={(event) => {
                          onClose?.();
                          if (active) {
                            event.preventDefault();
                            return;
                          }
                        }}
                      >
                        <Icon name="branch" />
                        {renamingWorktreeId === worktree.id ? (
                          <InlineRenameField
                            initialValue={worktree.name}
                            ariaLabel="Task name"
                            className="inline-rename-field sidebar-row-rename"
                            onCommit={(value) => { setRenamingWorktreeId(null); void renameWorktree(worktree, value).catch(() => undefined); }}
                            onCancel={() => setRenamingWorktreeId(null)}
                          />
                        ) : (
                          <span className="sidebar-row-label truncate" title={worktree.branch}>{worktree.name || worktree.branch}</span>
                        )}
                        <span className="sidebar-activity-slot" aria-hidden="true">
                          <WorktreeActivity chats={chats} worktree={worktree} />
                        </span>
                      </Link>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </div>

      <ContextMenu state={rowMenu.state} onClose={rowMenu.close} />

      <NewTaskModal
        open={newTaskOpen}
        onClose={() => setNewTaskOpen(false)}
        orgId={orgId}
        repos={repos}
        defaultRepoId={newTaskRepoId}
        initialKind={newTaskKind}
        onCreated={({ worktree, chat }) => {
          queryClient.setQueryData<{ worktrees: Worktree[] }>(qk.worktrees(worktree.repoId), (old) =>
            old ? { worktrees: [worktree, ...old.worktrees.filter((candidate) => candidate.id !== worktree.id)] } : { worktrees: [worktree] },
          );
          queryClient.setQueryData<WorktreeDetailResponse>(qk.worktree(worktree.id), {
            worktree,
            chats: [chat],
            latestSnapshotId: null,
            pullRequest: null,
          });
          setRepoCollapsed(orgId, worktree.repoId, false);
          onClose?.();
          navigate({ to: `/o/${orgId}/wt/${worktree.id}`, search: { chat: chat.id } });
        }}
      />
    </aside>
  );
}

/**
 * The history pair.
 *
 * Its own component because it is the one part of the header that changes on
 * every navigation: keeping the two booleans here means the rest of the
 * sidebar — the whole project tree — is not re-rendered when an arrow lights
 * up. `aria-disabled` rather than `disabled` so the control keeps its place in
 * the tab order and can still say why it does nothing.
 */
function HistoryArrows({ onClose }: { onClose?: () => void }) {
  const router = useRouter();
  const { canGoBack, canGoForward } = useNavigationHistory();
  const bindings = useKeyboardShortcutSettings((state) => state.bindings);

  useShortcutCommand("goBack", () => {
    if (!canGoBack) return false;
    router.history.back();
  });
  useShortcutCommand("goForward", () => {
    if (!canGoForward) return false;
    router.history.forward();
  });

  return (
    <>
      <button
        type="button"
        className="sidebar-header-button"
        aria-label="Back"
        aria-disabled={!canGoBack}
        title={`Back (${shortcutChordLabel(bindings.goBack)})`}
        onClick={() => { if (!canGoBack) return; router.history.back(); onClose?.(); }}
      >
        <Icon name="chevronLeft" />
      </button>
      <button
        type="button"
        className="sidebar-header-button"
        aria-label="Forward"
        aria-disabled={!canGoForward}
        title={`Forward (${shortcutChordLabel(bindings.goForward)})`}
        onClick={() => { if (!canGoForward) return; router.history.forward(); onClose?.(); }}
      >
        <Icon name="chevronRight" />
      </button>
    </>
  );
}

/** The assistant's mark: a small conversational spark, distinct from the
 * navigation glyph set so the drawer never reads as another destination. */
function AssistantGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 1.5 9.1 5 12.5 6 9.1 7 8 10.5 6.9 7 3.5 6 6.9 5 8 1.5Z" fill="currentColor" />
      <path d="M12.5 10.5l.55 1.7 1.7.55-1.7.55-.55 1.7-.55-1.7-1.7-.55 1.7-.55.55-1.7Z" fill="currentColor" opacity=".65" />
    </svg>
  );
}

function TreeLink({ to, active, icon, onClick, children }: { to: string; active: boolean; icon: NavigationIconName; onClick?: () => void; children: ReactNode }) {
  return (
    <Link to={to} activeOptions={{ exact: true }} className={`sidebar-tree-row sidebar-action-row${active ? " active" : ""}`} data-tree-row data-depth="0" onClick={onClick}>
      <Icon name={icon} /><span className="sidebar-row-label">{children}</span>
    </Link>
  );
}

function WorktreeActivity({ chats, worktree }: { chats: Chat[]; worktree: Worktree }) {
  const activity = worktreeActivity(worktree, chats);
  return activity ? <span className={`sidebar-activity ${activity.kind}`} title={activity.title} /> : null;
}

function handleTreeKeys(event: KeyboardEvent<HTMLElement>) {
  const target = event.target as HTMLElement;
  const rows = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("[data-tree-row]"));
  const index = rows.findIndex((row) => row === target || row.contains(target));
  if (index < 0) return;
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    const offset = event.key === "ArrowDown" ? 1 : -1;
    rows[(index + offset + rows.length) % rows.length]?.focus();
  } else if (event.key === "ArrowRight" && target.dataset.expandable === "true" && target.dataset.expanded !== "true") {
    event.preventDefault();
    target.click();
  } else if (event.key === "ArrowLeft") {
    if (target.dataset.expandable === "true" && target.dataset.expanded === "true") {
      event.preventDefault();
      target.click();
      return;
    }
    const depth = Number(target.dataset.depth ?? 0);
    if (depth > 0) {
      event.preventDefault();
      for (let previous = index - 1; previous >= 0; previous -= 1) {
        if (Number(rows[previous]?.dataset.depth ?? 0) < depth) { rows[previous]?.focus(); break; }
      }
    }
  } else if (event.key === "Enter") {
    event.preventDefault();
    target.click();
  }
}

function connectionTone(state: AppConnectionState): "green" | "amber" | "red" | "gray" {
  switch (state) {
    case "connected": return "green";
    case "connecting":
    case "reconnecting": return "amber";
    case "offline":
    case "updateRequired": return "red";
  }
}
